import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Loader2, Eye, Trash2, RotateCcw } from 'lucide-react';
import { UsersTableProps } from '../types';

export function UsersTable({
  isDark,
  users,
  isLoading,
  onView,
  onEdit,
  onDelete,
  onRestore,
  formatDate,
}: UsersTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className={cn(
          isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'
        )}>
          <TableHead className={cn(
            'font-semibold',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>ID</TableHead>
          <TableHead className={cn(
            'font-semibold',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Пользователь</TableHead>
          <TableHead className={cn(
            'font-semibold hidden md:table-cell',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Email</TableHead>
          <TableHead className={cn(
            'font-semibold',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Роль</TableHead>
          <TableHead className={cn(
            'font-semibold hidden lg:table-cell',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Статус</TableHead>
          <TableHead className={cn(
            'font-semibold hidden sm:table-cell',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Создан</TableHead>
          <TableHead className={cn(
            'font-semibold',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-12 text-slate-500">
              Пользователи не найдены
            </TableCell>
          </TableRow>
        ) : (
          users.map((user) => (
            <TableRow
              key={user.id}
              className={cn(
                'transition-colors',
                isDark
                  ? 'border-slate-700 hover:bg-slate-800/50'
                  : 'border-slate-100 hover:bg-slate-50',
                user.isDeleted && 'opacity-60'
              )}
            >
              <TableCell className={cn(
                'font-mono text-xs',
                isDark ? 'text-slate-400' : 'text-slate-500'
              )}>
                {user.id.substring(0, 8)}...
              </TableCell>
              <TableCell>
                <div>
                  <p className={cn(
                    'font-medium',
                    isDark ? 'text-white' : 'text-[#111111]'
                  )}>
                    {user.firstName && user.lastName 
                      ? `${user.firstName} ${user.lastName}`
                      : user.username}
                  </p>
                  <p className={cn(
                    'text-xs mt-0.5',
                    isDark ? 'text-slate-400' : 'text-slate-500'
                  )}>@{user.username}</p>
                </div>
              </TableCell>
              <TableCell className={cn(
                'hidden md:table-cell',
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>{user.email || '—'}</TableCell>
              <TableCell>
                <Badge className={cn(
                  'rounded-lg font-medium border-0',
                  user.role === 'admin'
                    ? 'bg-purple-500/10 text-purple-500'
                    : 'bg-blue-500/10 text-blue-500'
                )}>
                  {user.role}
                </Badge>
              </TableCell>
              <TableCell className="hidden lg:table-cell">
                <div className="flex flex-col gap-1">
                  <Badge className={cn(
                    'rounded-lg font-medium border-0 w-fit',
                    user.isSubscribed
                      ? 'bg-emerald-500/10 text-emerald-500'
                      : 'bg-slate-500/10 text-slate-500'
                  )}>
                    {user.isSubscribed ? 'С подпиской' : 'Бесплатный'}
                  </Badge>
                  {user.is_anonymous && (
                    <Badge className="rounded-lg font-medium border-0 w-fit bg-amber-500/10 text-amber-500">
                      Аноним
                    </Badge>
                  )}
                  {user.isDeleted && (
                    <Badge className="rounded-lg font-medium border-0 w-fit bg-red-500/10 text-red-500">
                      Удалён
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className={cn(
                'hidden sm:table-cell',
                isDark ? 'text-slate-400' : 'text-slate-500'
              )}>{formatDate(user.created_at)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onView(user)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark 
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-white' 
                        : 'text-slate-600 hover:bg-slate-100'
                    )}
                    title="Подробнее"
                    aria-label="Подробнее о пользователе"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  {!user.isDeleted && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(user)}
                      className={cn(
                        'h-8 px-2 rounded-lg',
                        isDark
                          ? 'text-slate-400 hover:bg-slate-800 hover:text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      )}
                      title="Редактировать"
                      aria-label="Редактировать пользователя"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  {user.isDeleted ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRestore(user)}
                      className={cn(
                        'h-8 px-2 rounded-lg text-emerald-500',
                        isDark ? 'hover:bg-slate-800' : 'hover:bg-emerald-50'
                      )}
                      title="Восстановить"
                      aria-label="Восстановить аккаунт"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(user)}
                      className={cn(
                        'h-8 px-2 rounded-lg text-red-500',
                        isDark ? 'hover:bg-slate-800' : 'hover:bg-red-50'
                      )}
                      title="Удалить аккаунт"
                      aria-label="Удалить аккаунт"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
